import React, { RefObject, useState, useMemo } from 'react';
import { PlanningPageType } from './PlanningPage';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  useIncidentCalendarEvents,
  IncidentCalendarDisplayEvent,
} from '../hooks/incidentCalendarHooks';
import CalendarEventEditDrawer from '../CalendarEventEditDrawer';
import IncidentObjectives from './widgets/IncidentObjectives';
import OpenActionsTracker from './widgets/OpenActionsTracker';
import ICSFormsExport from './widgets/ICSFormsExport';
import PlanningObjectives from './widgets/PlanningObjectives';
import OrganizationAssignmentList from './widgets/OrganizationAssignmentList';
import SafetyPlan from './widgets/SafetyPlan/SafetyPlan';
import IncidentStatusSummary from './widgets/IncidentStatusSummary/IncidentStatusSummary';
import AssignmentList from './widgets/AssignmentList/AssignmentList';
import IncidentRadioCommunicationsPlan from './widgets/IncidentRadioCommunicationsPlan';
import MedicalPlan from './widgets/MedicalPlan/MedicalPlan';
import IAPSafetyAnalysis from './widgets/IAPSafetyAnalysis';
import IncidentActionPlan from './widgets/IncidentActionPlan';
import { useUpdateOperationalPeriod } from 'hooks/incident/useIncidentManagement';
import { selectIncidentGuid } from 'slices/commonSelectors';
import { useCurrentPeriod, useIncident } from '../hooks/incidentHooks';
import { useSelector } from 'react-redux';
import InitialResponseBriefing from '../initialResponsePage/InitialResponseBriefing';
import { PlanningAgenda } from './PlanningAgenda';
import { Dropdown } from 'react-bootstrap';
import { PLANNING_PHASE, WIDGET_TITLES } from './USCGPage';
import UnifiedWorkView from './UnifiedWorkView';
import IndependentWorkAnalysisView from './IndependentWorkAnalysisView';
import IndependentWorkAssignmentView from './IndependentWorkAssignmentView';
import { useICSContext } from './ICSContext';
import classNames from 'classnames';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import { AdvanceOperationalPeriodModal } from '../AdvanceOperationalPeriodModal';
import HazardousMaterials from './widgets/HazardousMaterials/HazardousMaterials';
import CommunicationsList from './widgets/CommunicationsList';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
type CardOption = { label: string; value: string };

type PlanningSectionProps = {
  planningPhases: PlanningPageType[];
  currentPhase: number;
  updatePhase: (phase: number) => void;
  mainContentRef: RefObject<HTMLDivElement>;
};

const REMOVED_WIDGETS = ['agenda', 'initialResponse'];

const PlanningSection: React.FC<PlanningSectionProps> = ({
  planningPhases,
  currentPhase,
  updatePhase,
  mainContentRef,
}) => {
  const [showAgenda, setShowAgenda] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [
    showAdvanceOperationalPeriodModal,
    setShowAdvanceOperationalPeriodModal,
  ] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [meetingEvent, setMeetingEvent] =
    useState<IncidentCalendarDisplayEvent | null>(null);
  const [showMoreForms, setShowMoreForms] = useState(false);
  const [moreFormsSearchTerm, setMoreFormsSearchTerm] = useState('');

  const { data: calendarEvents } = useIncidentCalendarEvents();
  const currentPhaseData: PlanningPageType = planningPhases[currentPhase];
  const { card, setCard } = useICSContext();
  const widgetOrder = useMemo(() => {
    return (
      currentPhaseData.widgetOrder?.filter(
        (widgetName) => !REMOVED_WIDGETS.includes(widgetName)
      ) ||
      Object.keys(currentPhaseData.widgets) ||
      []
    );
  }, [currentPhaseData.widgetOrder, currentPhaseData.widgets]);

  const mutation = useUpdateOperationalPeriod();
  const incidentId = useSelector(selectIncidentGuid);
  const incident = useIncident();
  const currentPeriod = useCurrentPeriod();

  const widgetOrderWithTitles = useMemo(
    () =>
      widgetOrder.map((widgetName) => {
        const titleObj = WIDGET_TITLES.find((w) => w.widgetName === widgetName);
        return {
          widgetName,
          title: titleObj ? titleObj.title : widgetName,
        };
      }),
    [widgetOrder]
  );

  const dropdownOptions: CardOption[] = useMemo(
    () =>
      WIDGET_TITLES.filter(
        ({ widgetName }) =>
          !REMOVED_WIDGETS.includes(widgetName) &&
          !widgetOrder.includes(widgetName)
      ).map(({ widgetName, title }) => ({
        label: title,
        value: widgetName,
      })),

    [widgetOrder]
  );

  const filteredDropdownOptions = useMemo(
    () =>
      dropdownOptions.filter((opt) =>
        opt.label.toLowerCase().includes(moreFormsSearchTerm.toLowerCase())
      ),
    [dropdownOptions, moreFormsSearchTerm]
  );

  const selectedDropdownOption =
    dropdownOptions.find((opt) => opt.value === card) ?? null;

  const handleDropdownChange = (option: CardOption | null) => {
    setCard(option?.value ?? widgetOrder[0]);
  };

  //Always makes sure a card is rendered
  const widgetToRender = useMemo(() => {
    if (!widgetOrder.length) return '';

    if (!card) {
      const defaultWidget = widgetOrder[0];
      return defaultWidget;
    }

    return card;
  }, [widgetOrder, card]);

  const isLastPhase = currentPhase === planningPhases.length - 1;

  const handleCompletePhase = () => {
    if (!isLastPhase) {
      updatePhase(currentPhase + 1);
    }
    mutation.mutate({
      incidentId: incidentId,
      newEndTime:
        currentPeriod.currentPeriod?.anticipated_end_date_time || undefined,
      operationalPeriod: currentPeriod.currentPeriod?.period_number || 0,
      phaseNumberCompleted: currentPhase,
      showToast: false,
    });
    if (mainContentRef.current) {
      mainContentRef.current.scrollIntoView({
        block: 'start',
      });
    }
    setShowModal(false);
  };

  return (
    <>
      <div className="planning-section">
        <div className="d-flex align-items-center">
          <h2 className="me-2">{planningPhases[currentPhase].title}</h2>
          {currentPhaseData.agenda.length > 0 && (
            <StylishNewButton
              onClick={() => setShowAgenda(!showAgenda)}
              className={classNames('ms-1 button button--secondary', {
                'border-primary-700': showAgenda,
                'border-neutral-500': !showAgenda,
              })}
            >
              <SharedIcon iconName="checklist" classes="me-1" />
              Agenda
            </StylishNewButton>
          )}
          <StylishNewButton
            onClick={() => {
              const event = calendarEvents?.find(
                (ev) => ev.eventTypeLabel === currentPhaseData.title
              );
              if (event) {
                setMeetingEvent(event);
                setShowEditDrawer(true);
              }
            }}
            disabled={
              !calendarEvents?.some(
                (ev) => ev.eventTypeLabel === currentPhaseData.title
              )
            }
            className="ms-1 button button--secondary"
          >
            <SharedIcon iconName="calendar_today" classes="me-1" />
            Meeting Details
          </StylishNewButton>
          <StylishNewButton
            onClick={() => setShowModal(true)}
            disabled={
              (currentPeriod.currentPeriod?.phase_number_completed != null &&
                currentPhase - 1 !==
                  currentPeriod.currentPeriod?.phase_number_completed) ||
              (currentPeriod.currentPeriod?.phase_number_completed == null &&
                currentPhase !== 0)
            }
            className="ms-1 me-1 button button--secondary"
          >
            <SharedIcon iconName="check_circle" classes="me-1" />
            Complete Phase
          </StylishNewButton>
          {currentPhaseData.title === PLANNING_PHASE.OPERATIONS_BRIEFING && (
            <StylishNewButton
              className="button--secondary"
              onClick={() => setShowAdvanceOperationalPeriodModal(true)}
            >
              Advance Operational Period
            </StylishNewButton>
          )}
        </div>
        <div className="description">
          {planningPhases[currentPhase].description}
        </div>
        <div className="d-flex flex-row flex-wrap align-items-center">
          {widgetOrderWithTitles.map(({ widgetName, title }) => (
            <StylishNewButton
              key={widgetName + '-button'}
              type="button"
              data-testid={`${widgetName}-widget-button`}
              className={classNames('button button--secondary mb-2 me-2', {
                'border-primary-700': card === widgetName,
              })}
              onClick={() => setCard(widgetName)}
            >
              {title}
            </StylishNewButton>
          ))}
          {dropdownOptions.length > 0 && (
            <Dropdown
              className="d-inline-block mb-2"
              onToggle={setShowMoreForms}
            >
              <Dropdown.Toggle
                className={classNames('button button--secondary', {
                  'border-primary-700': !!selectedDropdownOption,
                })}
                id="more-cards-dropdown"
              >
                <>
                  {selectedDropdownOption
                    ? `More ICS Forms: ${selectedDropdownOption.label}`
                    : 'More ICS Forms'}
                  {selectedDropdownOption && (
                    <span
                      className="ms-2 d-inline-flex lh-1 planning-card-clear"
                      role="button"
                      aria-label="Clear selection"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDropdownChange(null);
                      }}
                    >
                      <SharedIcon iconName="close" size="sm" />
                    </span>
                  )}
                  <SharedIcon
                    iconName={`arrow_drop_${showMoreForms ? 'up' : 'down'}`}
                    classes="ms-1"
                  />
                </>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <StylishNewSearchBar
                  value={moreFormsSearchTerm}
                  onChangeSearchTerm={setMoreFormsSearchTerm}
                  placeholder="Search Section Templates..."
                />
                {filteredDropdownOptions.map((opt) => (
                  <Dropdown.Item
                    key={opt.value}
                    active={card === opt.value}
                    onClick={() => handleDropdownChange(opt)}
                  >
                    {opt.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
        <div className="d-flex gap-2">
          {showAgenda && (
            <div className="w-50">
              <PlanningAgenda
                planningPhases={planningPhases}
                currentPhase={currentPhase}
              />
            </div>
          )}
          <div className={showAgenda ? 'w-50' : 'w-100'}>
            {renderWidget(
              widgetToRender,
              planningPhases,
              currentPhase,
              currentPeriod
            )}
          </div>
        </div>

        <div
          className={
            currentPhase > 0
              ? 'd-flex justify-content-between mt-3'
              : 'd-flex justify-content-end mt-3'
          }
        >
          {currentPhase > 0 && (
            <StylishNewButton
              className="button button--secondary"
              onClick={() => updatePhase(currentPhase - 1)}
            >
              <SharedIcon iconName="arrow_back" classes="me-2" />
              Previous Phase
            </StylishNewButton>
          )}
        </div>
      </div>
      <CustomModal
        show={showModal}
        heading={'Complete Phase ' + (currentPhase + 1) + '?'}
        onClose={() => setShowModal(false)}
        onConfirm={handleCompletePhase}
        saveButton={'Complete Phase ' + (currentPhase + 1)}
        buttonDisable={false}
        icon={<SharedIcon iconName="warning" color="primary" size="lg" />}
        staticBackdrop={false}
      >
        {!isLastPhase ? (
          <div className="p-2">
            You are about to complete Phase {currentPhase + 1} and advance to
            Phase {currentPhase + 2}.
          </div>
        ) : (
          <div className="p-2">
            You are about to complete Phase {currentPhase + 1}.
          </div>
        )}
      </CustomModal>
      {showAdvanceOperationalPeriodModal && (
        <AdvanceOperationalPeriodModal
          showModal={showAdvanceOperationalPeriodModal}
          setShowModal={setShowAdvanceOperationalPeriodModal}
          data={incident.data}
        />
      )}
      {showEditDrawer && meetingEvent && (
        <CalendarEventEditDrawer
          toggle={() => {
            setShowEditDrawer(false);
            setMeetingEvent(null);
          }}
          selectedEntity={meetingEvent}
        />
      )}
    </>
  );
};

export const renderWidget = (
  widgetName: string,
  planningPhases: PlanningPageType[],
  currentPhase: number,
  currentPeriod: ReturnType<typeof useCurrentPeriod>
) => {
  switch (widgetName) {
    case 'agenda':
      return (
        <PlanningAgenda
          planningPhases={planningPhases}
          currentPhase={currentPhase}
        />
      );
    case 'incidentObjectives':
      return <IncidentObjectives />;
    case 'incidentActionPlan':
      return (
        <IncidentActionPlan
          currPeriod={currentPeriod.currentPeriod}
          isFuture={true}
        />
      );
    case 'workAnalysis':
      return <UnifiedWorkView />;
    case 'workAnalysisMatrix':
      return <IndependentWorkAnalysisView />;
    case 'operationalPlanningWorksheet':
      return <IndependentWorkAssignmentView />;
    case 'icsFormExport':
      return <ICSFormsExport />;
    case 'openActionsTracker':
      return <OpenActionsTracker />;
    case 'iapSafetyAnalysis':
      return <IAPSafetyAnalysis />;
    case 'initialResponse':
      return <InitialResponseBriefing />;
    case 'objectives':
      return <PlanningObjectives />;
    case 'organizationAssignmentList':
      return <OrganizationAssignmentList />;
    case 'safetyPlan':
      return <SafetyPlan />;
    case 'assignmentList':
      return (
        <AssignmentList
          periodNumber={(currentPeriod.currentPeriod?.period_number ?? 0) + 1}
        />
      );
    case 'incidentRadioCommunicationsPlan':
      return <IncidentRadioCommunicationsPlan />;
    case 'communicationsList':
      return <CommunicationsList />;
    case 'medicalPlan':
      return <MedicalPlan />;
    case 'incidentStatusSummary':
      return <IncidentStatusSummary currPeriod={currentPeriod.currentPeriod} />;
    case 'hazardousMaterials':
      return <HazardousMaterials />;
    default:
      return null;
  }
};
export default PlanningSection;
